/**
 * byo-v4-fill.js — the Fill stage (P3a).
 *
 * Ported by name from the design so a reviewer can diff component for
 * component: `useCatalog`, `CategoryTabs`, `ProductGrid`, `ProductCard` from
 * `byo-core-v2.jsx`, and the shelves from `concept-e-shelves-v2.jsx`
 * (`ConceptShelves`, the `stage === 'fill'` branch). DESIGN-SPEC.md §3.2, §3.3.
 *
 * P3a is the split point in BUILD-PLAN.md: shelves, tabs, grid, cards,
 * add/remove and the refusal. **Not** in this file yet, by that split:
 * Quick Look, flyToBox and the price callout (P3b), and the build bar,
 * progress rail and help sheet (P4). Where the design reaches for one of
 * those, the seam is marked rather than stubbed with something that pretends
 * to work.
 *
 * ES2020 module. View layer: assets/byo-v4-vendor.js (Preact + htm).
 */

import { html, render, useState, useMemo, useRef, useEffect, useLayoutEffect, useCallback } from './byo-v4-vendor.js';
const { Icon, Btn, Pill, Toast, ProductImage, SizeChip, flyToBox } = (window.BYOV4 = window.BYOV4 || {});
const { QuickView } = (window.BYOV4 = window.BYOV4 || {});
const { BuildBar } = (window.BYOV4 = window.BYOV4 || {});
const { ProgressRail, STAGE_TITLES, nextFor, canGo, gateReason, prevStage, nextStage } = (window.BYOV4 = window.BYOV4 || {});
const { InsideSheet, HelpSheet, Sheet } = (window.BYOV4 = window.BYOV4 || {});
const { PersonaliseStage } = (window.BYOV4 = window.BYOV4 || {});
const { ConfirmStage } = (window.BYOV4 = window.BYOV4 || {});
const { StageHead } = (window.BYOV4 = window.BYOV4 || {});
const { submitBox, updateBox, newBundleKey, loadCart, findBundleLine, bundleName, refreshCartCount } =
  (window.BYOV4 = window.BYOV4 || {});
const { AddedScreen } = (window.BYOV4 = window.BYOV4 || {});
/** Shelf order, top to bottom — concept-e-shelves-v2.jsx:2. */
export const SHELF_ORDER = ['Extra Large', 'Large', 'Medium', 'Small'];

/* D64: the card's tags are short, so two or three fit on a phone-width
   picture. The full word stays for screen readers. */
export const SIZE_SHORT = { 'Extra Large': 'XL', Large: 'L', Medium: 'M', Small: 'S' };
const DIET_TAGS = [
  { id: 'Vegan', short: 'Vegan', full: 'Vegan' },
  { id: 'Alcohol free', short: 'AF', full: 'Alcohol-free' },
];

/* D72: the sorts the results line offers. "Recommended" is the order set on
   each collection in the admin (D70); the rest reorder whatever the filters
   leave, and a tie keeps the recommended order. */
export const SORTS = [
  { id: 'recommended', label: 'Recommended' },
  { id: 'price-asc', label: 'Lowest price' },
  { id: 'price-desc', label: 'Highest price' },
  { id: 'size-asc', label: 'Smallest first' },
  { id: 'size-desc', label: 'Largest first' },
];
const SIZE_RANK = { Small: 1, Medium: 2, Large: 3, 'Extra Large': 4 };
const SORT_BY = {
  'price-asc': (x, y) => x.price - y.price,
  'price-desc': (x, y) => y.price - x.price,
  'size-asc': (x, y) => (SIZE_RANK[x.size] || 0) - (SIZE_RANK[y.size] || 0),
  'size-desc': (x, y) => (SIZE_RANK[y.size] || 0) - (SIZE_RANK[x.size] || 0),
};

/* The photo upload's two waits (`uploadPhoto`). A minute with no bytes leaving
   the phone is a dead connection, not a slow one. Two minutes for Shopify to
   answer once it has every byte is far past the few seconds it takes to store
   a photo, and cutting it short risks a line it adds after we have gone. */
const UPLOAD_STALL_MS = 60000;
const UPLOAD_SETTLE_MS = 120000;

/**
 * Filtering. The design filters on `p.category`; ours filters on the
 * collection handles a product was merged from, because the pills are
 * collections (CONFIG-SCHEMA.md §2) and normalise tags each product with the
 * handle it came from.
 *
 * Dietary matching is **inclusive**: a product matches "Vegan" by carrying
 * `Vegan` in `custom.dietaries_or_allergens`. The design instead treated
 * alcohol-free as the absence of an `alcohol` marker, which our data has no
 * equivalent for — the real metafield carries a positive `Alcohol free`
 * literal on 18 products. So an untagged product does not match a dietary
 * filter, which OPEN-QUESTIONS.md #12 explicitly sanctions: "untagged products
 * simply do not match, and the filter is never presented as exhaustive".
 * SPIKE-FINDINGS.md C14 for the literals.
 */
export function useCatalog(products, b) {
  const [cat, setCat] = useState('All');
  const [diet, setDiet] = useState([]);
  const [sort, setSort] = useState('recommended');

  /* fitOnly lives on the builder, not here, exactly as the design has it
     (byo-core-v2.jsx:130-132): the refusal toast's "Show what fits" action is
     raised from `add`, which has no way to reach this hook's state. */
  const fitOnly = !!b.fitOnly;

  /* A pill lists its products in the order the merchant set on that
     collection in the admin (D70). "All" keeps the pills' order, each
     collection's products in its own order. */
  const list = useMemo(() => {
    const matched = products.filter(
      (p) =>
        (cat === 'All' || (p.collections || []).includes(cat)) &&
        diet.every((d) => (p.dietaries || []).includes(d)) &&
        (!fitOnly || b.fitsSize(p.size))
    );
    const at = (p) => (p.order && typeof p.order[cat] === 'number' ? p.order[cat] : Number.MAX_SAFE_INTEGER);
    const ordered = cat === 'All' ? matched : matched.slice().sort((x, y) => at(x) - at(y));
    /* Array sort is stable, so a tie keeps the recommended order. */
    return SORT_BY[sort] ? ordered.slice().sort(SORT_BY[sort]) : ordered;
  }, [products, cat, diet, sort, fitOnly, b.remaining, b.itemCount]);

  return {
    cat,
    setCat,
    diet,
    toggleDiet: (d) => setDiet((ds) => (ds.includes(d) ? ds.filter((x) => x !== d) : [...ds, d])),
    list,
    sort,
    setSort,
    fitOnly,
    setFitOnly: (v) => b.setFitOnly(v),
    clear: () => {
      setCat('All');
      setDiet([]);
      b.setFitOnly(false);
    },
    filtered: cat !== 'All' || diet.length > 0 || fitOnly,
  };
}

/**
 * One scrolling line: collection pills, a divider, the dietary chips, then
 * "Fits my box" once the box has something in it. The right-edge fade carries
 * a chevron and fades out when the row is scrolled to its end.
 */
export function CategoryTabs({ c, config, hasItems }) {
  const [edge, setEdge] = useState(true);
  const row = useRef(null);

  const onScroll = useCallback(() => {
    const el = row.current;
    if (el) setEdge(el.scrollWidth - el.clientWidth - el.scrollLeft > 6);
  }, []);

  useEffect(() => {
    onScroll();
  }, [onScroll, c.list.length]);

  const collections = [{ handle: 'All', label: 'All' }, ...(config.collections || [])];

  return html`
    <div class="byo-v4-tabs">
      <div class="byo-v4-tabs__row" ref=${row} onScroll=${onScroll}>
        ${collections.map(
          (k) => html`
            <button
              type="button"
              key=${k.handle}
              class=${`byo-v4-tab${c.cat === k.handle ? ' is-on' : ''}`}
              aria-pressed=${c.cat === k.handle ? 'true' : 'false'}
              onClick=${() => c.setCat(k.handle)}
            >${k.label}</button>
          `
        )}

        <span class="byo-v4-tabs__divider" aria-hidden="true"></span>

        ${(config.dietaries || []).map((d) => {
          const on = c.diet.includes(d.id);
          return html`
            <button
              type="button"
              key=${d.id}
              class=${`byo-v4-chip${on ? ' is-on' : ''}`}
              aria-pressed=${on ? 'true' : 'false'}
              onClick=${() => c.toggleDiet(d.id)}
            >${on && html`<${Icon} name="check" size=${12} stroke=${3} />`}${d.label}</button>
          `;
        })}

        ${hasItems &&
        html`
          <button
            type="button"
            class=${`byo-v4-chip${c.fitOnly ? ' is-on' : ''}`}
            aria-pressed=${c.fitOnly ? 'true' : 'false'}
            onClick=${() => c.setFitOnly(!c.fitOnly)}
          >${c.fitOnly && html`<${Icon} name="check" size=${12} stroke=${3} />`}Fits my box</button>
        `}
      </div>

      <div class=${`byo-v4-tabs__fade${edge ? '' : ' is-off'}`} aria-hidden="true">
        <${Icon} name="chevR" size=${16} />
      </div>
    </div>
  `;
}

/**
 * ResultsLine — D72 (Alan, 26 Sep 2026: "a product counter so that it's always
 * obvious how many products are in that selection, so customers know to
 * scroll down", and a quick way to sort by size or price).
 *
 * One line under the pills, inside the sticky block so it stays in view while
 * the grid scrolls: how many products the pills and filters leave, in words,
 * and a Sort pill in the same shape as the filter chips. The pill is a native
 * <select> laid over a chip, so a phone opens its own picker and a keyboard
 * works as it would on any select; it is lime while it sorts, like a chip that
 * is on.
 */
/* Browse mode (D129): on a phone the frame is a full screen tall, so docked
   under the theme's header its foot hangs below the screen. This is how far,
   up to the header's height; the bar, its fade and the sheets are lifted by
   it (--byo-v4-dock), by transform only. */
function overhang(frame, hh) {
  if (!frame || !window.matchMedia('(max-width: 989.98px)').matches) return 0;
  const over = frame.getBoundingClientRect().bottom - window.innerHeight;
  return Math.min(hh, Math.max(0, over));
}

/* The lift is written onto the few elements that use it, never onto the
   section: a custom property changed there restyles every product card
   (35ms a time in Safari with the full range shown), and near the dock it
   changes on every scroll event, which is what made the page judder on its
   way into the builder (Alan's iPhone, 11 Oct). Unchanged values are skipped. */
let dockPx = 0;
function applyDock(frame, px) {
  dockPx = px;
  const v = `${px.toFixed(2)}px`;
  frame.querySelectorAll('.byo-v4-bar, .byo-v4-stage__fade, .byo-v4-sheet').forEach((el) => {
    if (el.style.getPropertyValue('--byo-v4-dock') !== v) el.style.setProperty('--byo-v4-dock', v);
  });
}

/* True under the phone breakpoint, following the window across it. */
function usePhone() {
  const q = '(max-width: 989.98px)';
  const [on, setOn] = useState(() => window.matchMedia(q).matches);
  useEffect(() => {
    const m = window.matchMedia(q);
    const f = () => setOn(m.matches);
    if (m.addEventListener) m.addEventListener('change', f);
    else m.addListener(f);
    return () => (m.removeEventListener ? m.removeEventListener('change', f) : m.removeListener(f));
  }, []);
  return on;
}

/* D131: draws its children into the stage's head band, under the rail
   ([data-byo-v4-head-slot]), the way Layer draws into <body>. On a phone the
   chip row lives there, outside the scrolling list: pinned inside the list
   with position: sticky, iPhone Safari let it shiver a pixel up and down as
   the products scrolled, with a sliver of them showing above it (Alan,
   11 Oct). Out of the scroller it cannot move at all. */
function HeadSlot({ children }) {
  const host = useRef(null);
  useLayoutEffect(() => {
    const slot = document.querySelector('[data-byo-v4-head-slot]');
    if (!slot) return undefined;
    const el = document.createElement('div');
    el.className = 'byo-v4-fill__inner';
    slot.appendChild(el);
    host.current = el;
    return () => {
      render(null, el);
      el.remove();
      host.current = null;
    };
  }, []);
  useLayoutEffect(() => {
    if (host.current) render(children, host.current);
  });
  return null;
}

function countLabel(c) {
  const n = c.list.length;
  const W = window.BYO && window.BYO.Words ? window.BYO.Words : String;
  return n ? `${W(n)} product${n === 1 ? '' : 's'}` : 'No products';
}

export function ResultsLine({ c }) {
  const current = SORTS.find((x) => x.id === c.sort) || SORTS[0];
  return html`
    <div class="byo-v4-results">
      <span class="byo-v4-results__count" aria-live="polite">${countLabel(c)}</span>
      <label class=${`byo-v4-sort${c.sort !== 'recommended' ? ' is-on' : ''}`}>
        <span class="byo-v4-sort__k" aria-hidden="true">Sort:</span>
        <span class="byo-v4-sort__v" aria-hidden="true">${current.label}</span>
        <${Icon} name="chevD" size=${14} stroke=${2.5} />
        <select
          class="byo-v4-sort__select"
          aria-label="Sort products"
          value=${c.sort}
          onChange=${(e) => c.setSort(e.target.value)}
        >
          ${SORTS.map((x) => html`<option key=${x.id} value=${x.id}>${x.label}</option>`)}
        </select>
      </label>
    </div>
  `;
}

/**
 * SortPill — the Sort control at the end of the pinned chip row, on phones
 * only (D129, Alan, 10 Oct 2026: more of the screen for products). There the
 * results line above scrolls away with the heading, so Sort moves in beside
 * the chips that stay. The same native <select> over a chip as ResultsLine's;
 * lime while it sorts.
 */
export function SortPill({ c }) {
  const current = SORTS.find((x) => x.id === c.sort) || SORTS[0];
  return html`
    <label class=${`byo-v4-sort byo-v4-sort--pin${c.sort !== 'recommended' ? ' is-on' : ''}`}>
      <span class="byo-v4-sort__k" aria-hidden="true">Sort</span>
      <${Icon} name="chevD" size=${14} stroke=${2.5} />
      <select
        class="byo-v4-sort__select"
        aria-label=${`Sort products: ${current.label}`}
        value=${c.sort}
        onChange=${(e) => c.setSort(e.target.value)}
      >
        ${SORTS.map((x) => html`<option key=${x.id} value=${x.id}>${x.label}</option>`)}
      </select>
    </label>
  `;
}

/**
 * The card. `live` is the design's: in items mode the plus stays enabled even
 * when the item will not fit, so that tapping it produces the refusal rather
 * than nothing at all — which is the whole point of the refusal toast.
 */
export function ProductCard({ p, b, noSize }) {
  const qty = b.qtyOf(p.id);
  const ok = b.fits(p);
  const why = b.reason(p);
  const live = ok || b.itemsMode;
  const shaking = b.shake && b.shake.startsWith(`${p.id}:`);

  return html`
    <div
      class=${`byo-v4-card${shaking ? ' is-shaking' : ''}${!ok && qty === 0 ? ' is-unfit' : ''}`}
      data-byo-card
      data-byo-handle=${p.handle}
      key=${shaking ? b.shake : 'still'}
    >
      ${/* The image is the Quick Look control — DESIGN-SPEC.md §3.3 "tappable
           (opens Quick Look)". A card that will not fit stays tappable: the
           refusal wash is a label, not a lock. */ ''}
      <button
        type="button"
        class="byo-v4-card__media"
        onClick=${() => b.openPeek(p.id)}
        aria-label=${`Quick look: ${p.title}`}
      >
        <${ProductImage} p=${p} />
        ${!noSize &&
        html`<div class="byo-v4-card__size">
          <${Pill} tone="lime">
            <span aria-hidden="true">${`${SIZE_SHORT[p.size] || p.size}${b.itemsMode ? '' : ` · ${b.slotWord(p.size)}`}`}</span
            ><span class="byo-v4-sr">${b.itemsMode ? p.size : `${p.size} · ${b.slotWord(p.size)}`}</span>
          <//>
        </div>`}
        ${DIET_TAGS.some((d) => (p.dietaries || []).includes(d.id)) &&
        html`<div class="byo-v4-card__diet">
          ${DIET_TAGS.filter((d) => (p.dietaries || []).includes(d.id)).map(
            (d) => html`<${Pill} key=${d.id} tone="white" class="byo-v4-card__dtag">
              ${d.short === d.full
                ? d.full
                : html`<span aria-hidden="true">${d.short}</span><span class="byo-v4-sr">${d.full}</span>`}
            <//>`
          )}
        </div>`}
        ${!ok &&
        qty === 0 &&
        html`
          <div class="byo-v4-card__wash">
            <${Pill} tone="plum" class="byo-v4-card__why">${why}<//>
          </div>
        `}
      </button>

      <div class="byo-v4-card__body">
        <div class="byo-v4-card__vendor">${p.vendor}</div>
        <div class="byo-v4-card__title">${p.title}</div>
        <div class="byo-v4-card__foot">
          <span class="byo-v4-card__price">${b.money(p.price)}</span>
          ${qty > 0
            ? html`
                <div class="byo-v4-stepper">
                  <button
                    type="button"
                    class="byo-v4-stepper__btn"
                    aria-label=${`Remove one ${p.title}`}
                    onClick=${() => b.remove(p.id)}
                  ><${Icon} name="minus" size=${16} stroke=${2.5} /></button>
                  <span class="byo-v4-stepper__count" aria-live="polite">${qty}</span>
                  <button
                    type="button"
                    class=${`byo-v4-stepper__btn${live ? '' : ' is-dim'}`}
                    aria-label=${`Add one more ${p.title}`}
                    onClick=${(e) => b.add(p.id, e.currentTarget)}
                  ><${Icon} name="plus" size=${16} stroke=${2.5} /></button>
                </div>
              `
            : html`
                <button
                  type="button"
                  class=${`byo-v4-add${live ? '' : ' is-disabled'}`}
                  aria-label=${`Add ${p.title} to your box`}
                  onClick=${(e) => b.add(p.id, e.currentTarget)}
                ><${Icon} name="plus" size=${18} stroke=${2.5} /></button>
              `}
        </div>
      </div>
    </div>
  `;
}

/* Cards drawn at first, and added each time the end of a grid comes near. */
const REVEAL = 12;

/**
 * The grid reveals its cards as the customer scrolls, twelve at a time
 * (Alan, 25 Sep 2026: "products revealed as the user scrolls down"). Every
 * shelf used to draw every card at once — up to 50 per collection, six
 * collections — and every tap on "+" then re-rendered them all, which is what
 * made a phone lag. The sentinel after the grid is watched 1200px early, so
 * the next cards are in place before they are scrolled to; the shelf's count
 * ("N to choose from") is still the whole shelf.
 *
 * `resetKey` is the filter (pill, dietaries, "Fits my box"): a new filter
 * starts from the top again. The list itself changes on every add, because
 * what fits changes, and that must not fold a grid the customer is halfway
 * down. Without IntersectionObserver every card is drawn, as before.
 */
export function ProductGrid({ list, b, c, noSize, resetKey }) {
  const [shown, setShown] = useState(REVEAL);
  const sentinel = useRef(null);
  const canReveal = typeof IntersectionObserver !== 'undefined';
  const more = canReveal && shown < list.length;

  useEffect(() => {
    setShown(REVEAL);
  }, [resetKey]);

  /* Re-armed after every reveal: an observer only reports a change, so a
     sentinel still in range after twelve more cards would otherwise never
     ask again. */
  useEffect(() => {
    if (!more || !sentinel.current) return undefined;
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) setShown((n) => n + REVEAL);
      },
      { rootMargin: '1200px 0px' }
    );
    io.observe(sentinel.current);
    return () => io.disconnect();
  }, [more, shown]);

  if (!list.length) {
    /* D70: no "Nothing here fits those filters yet". "Fits" is the box's
       word, and on a filter it only confused the two. */
    return html`
      <div class="byo-v4-empty">
        <div class="byo-v4-empty__head">No Matches</div>
        <${Btn} variant="outline" size="sm" onClick=${c.clear}>Clear Filters<//>
      </div>
    `;
  }

  return html`
    <div class="byo-v4-grid" data-byo-count=${list.length}>
      ${(canReveal ? list.slice(0, shown) : list).map((p) => html`<${ProductCard} key=${p.id} p=${p} b=${b} noSize=${noSize} />`)}
    </div>
    ${more && html`<div class="byo-v4-grid__more" ref=${sentinel} aria-hidden="true"></div>`}
  `;
}

/**
 * The shelves. A shelf is open by default while its size still fits and folds
 * shut when it stops fitting — but a manual open or close wins from then on,
 * which is why `manual` is keyed by size and only consulted when set.
 */
export function Shelves({ c, b }) {
  const [manual, setManual] = useState({});

  const shelves = SHELF_ORDER.map((size) => ({
    size,
    items: c.list.filter((p) => p.size === size),
  })).filter((s) => s.items.length);

  if (!shelves.length) return html`<${ProductGrid} list=${[]} b=${b} c=${c} />`;

  /* A new filter starts every grid from the top again (ProductGrid). */
  const resetKey = `${c.cat}|${(c.diet || []).join(',')}|${c.fitOnly ? 1 : 0}`;

  return html`
    ${shelves.map(({ size, items }) => {
      const fits = b.fitsSize(size);
      const isOpen = manual[size] != null ? manual[size] : fits;
      const panelId = `byo-v4-shelf-${size.replace(/\s+/g, '-').toLowerCase()}`;

      return html`
        <section class="byo-v4-shelf" key=${size}>
          <button
            type="button"
            class=${`byo-v4-shelf__head${isOpen ? ' is-open' : ''}${fits || isOpen ? '' : ' is-dim'}`}
            aria-expanded=${isOpen ? 'true' : 'false'}
            aria-controls=${panelId}
            onClick=${() => setManual((m) => ({ ...m, [size]: !isOpen }))}
          >
            <span class="byo-v4-shelf__label">
              <span class="byo-v4-shelf__size">${size}</span>
              <span class="byo-v4-shelf__count">
                ${b.itemsMode ? '' : `${b.slotWord(size)} each · `}${items.length} to choose from
              </span>
            </span>

            ${fits
              ? html`<${Pill} tone="lime">${b.itemsMode ? 'Fits' : `Room for ${Math.floor((b.remaining + 1e-9) / b.slotsOf(size))}`}<//>`
              : html`<${Pill} tone="orange">${b.itemsMode ? 'Box too full' : `No room for ${size} now`}<//>`}

            <span class="byo-v4-shelf__chev" aria-hidden="true">
              <${Icon} name=${isOpen ? 'chevU' : 'chevD'} size=${18} stroke=${2.5} />
            </span>
          </button>

          ${isOpen &&
          html`
            <div class="byo-v4-shelf__body" id=${panelId}>
              <${ProductGrid} list=${items} b=${b} c=${c} noSize resetKey=${resetKey} />
            </div>
          `}
        </section>
      `;
    })}
  `;
}

/**
 * The Fill stage shell: the sticky block (heading + tabs) and the shelves.
 *
 * The Help pill belongs beside the heading (DESIGN-SPEC.md §3.2) and arrives
 * with the Help sheet in P4; the heading currently sits alone rather than next
 * to a button that opens nothing.
 */
/**
 * The builder: P2's box store and packer, wired to the names the design's
 * components expect. P4 lifts this out when the build bar and the progress
 * rail need it too.
 *
 * The refusal is the whole reason `add` is not just `store.addItem`. Copy is
 * the design's, verbatim (byo-core-v2.jsx:75): one short line, the card shakes,
 * and — only when there is still room for something — a "Show what fits"
 * action. When the box is simply full there is nothing to show, so no action.
 */
export function useBuilder(config, products) {
  const store = useMemo(() => {
    const s = window.BYO.createBoxStore({ cfg: config, productsById: {} });
    return s;
  }, [config]);

  const byId = useMemo(() => Object.fromEntries(products.map((p) => [p.id, p])), [products]);
  const [state, setState] = useState(() => store.getState());
  const [shake, setShake] = useState(null);
  const [toast, setToast] = useState(null);
  const [fitOnly, setFitOnly] = useState(false);
  const [peek, setPeek] = useState(null);
  const toastTimer = useRef(null);

  useEffect(() => {
    store.setProducts(byId);
    return store.subscribe(setState);
  }, [store, byId]);

  useEffect(() => () => clearTimeout(toastTimer.current), []);

  const flash = useCallback((msg, ms, action, tone) => {
    clearTimeout(toastTimer.current);
    setToast({ msg, action, tone });
    toastTimer.current = setTimeout(() => setToast(null), ms);
  }, []);

  const packer = store.packer;
  const itemsMode = config.countUi === 'items';

  const b = {
    ...state,
    itemsMode,
    shake,
    toast,
    fitOnly,
    setFitOnly,
    byId,
    peek,
    openPeek: (id) => setPeek(id),
    closePeek: () => setPeek(null),
    capacity: config.capacity,
    minItems: config.minItems,
    minimum: config.minimumSpaces,
    /* ShippingBar tracks this parcel alone while building (DESIGN-SPEC §3.4.2),
       so its total is the box total the store already derives. */
    shipTotal: state.total,
    freeShipping: config.freeShipping,
    freeSpeed: config.freeSpeed,
    shipStyle: config.shipStyle,
    shipIcon: config.shipIcon,
    flashError: (msg) => flash(msg, 2600, null, 'error'),
    /* The deep link's arrival note (?add=, below): the neutral toast, held a
       little longer than an error because it is the first thing they read. */
    flashNote: (msg, ms) => flash(msg, ms || 4200, null, 'note'),
    /* A fresh box with these in it, in order. Returns the ones that went in:
       one that does not fit is skipped rather than failing the rest. */
    addMany: (ids) => {
      store.reset();
      return ids.filter((id) => byId[id] && store.addItem(byId[id]).ok);
    },

    /* --- personalisation writers (P5) ---------------------------------- */
    toggle: (key) => store.toggle(key),
    setInPerson: (v) => store.setInPerson(v),
    setKeepsake: (v) => {
      if (!!state.box.keepsake !== !!v) store.toggle('keepsake');
    },
    setWrap: (name) => store.setWrap(name),
    setPhoto: (patch) => store.setPhoto(patch),
    setSong: (patch) => store.setSong(patch),
    setDate: (patch) => store.setDate(patch),
    setMessage: (text) => store.setMessage(text),
    setFrom: (text) => store.setFrom(text),
    setRecipient: (text) => store.setRecipient(text),

    /**
     * The photo must reach the cart as a resolvable URL, so it is uploaded when
     * chosen rather than held for the write.
     *
     * **There is no Giftship upload endpoint.** This asked an invented proxy
     * URL and had never once answered — OPEN-QUESTIONS.md #1 recorded that the
     * premise was wrong and the spike documented what Giftship actually does
     * (GIFTSHIP-CONTRACT.md, "Photo upload"): Shopify's own cart accepts a
     * `File` as a line-item property, so a temporary line is added carrying the
     * file, the CDN URL is read back off the response, and the line is removed.
     *
     * Two departures from the stock builder, both deliberate:
     *   - it removes the temp line with `/cart/change.js`; that endpoint
     *     destroys bundle lines (GIFTSHIP-CONTRACT.md's correction), so this
     *     uses `/cart/update.js`, which does not.
     *   - the header stops Giftship's drawer opening on the temp line, which is
     *     the same reason the stock builder sends it.
     *
     * It is an XMLHttpRequest rather than `fetch` for one reason: `fetch` has
     * no upload progress, and a 6MB phone photo on mobile data sat on a static
     * "Uploading…" for long enough that it looked broken (D55). `onProgress`
     * is optional and gets `{ phase: 'sending', loaded, total, pct }` while
     * bytes leave the phone, then `{ phase: 'finishing', pct: 100 }` once they
     * have all gone and Shopify is still storing the file — the part with no
     * bytes to count, which can take a few seconds on its own.
     */
    uploadPhoto: async (file, onProgress) => {
      const variantId = config.baseVariantId;
      if (!variantId) throw new Error('no base variant to upload against');

      const body = new FormData();
      body.append('id', String(variantId));
      body.append('quantity', '1');
      body.append('properties[Photo]', file, file.name);
      body.append('properties[_tempUpload]', 'true');

      const report = typeof onProgress === 'function' ? onProgress : () => {};

      /* Temp lines out, three tries. `keys` are the lines this upload is known
         to have made; without them the cart is read, and every temp line in it
         goes. */
      const dropTemp = async (keys) => {
        for (let attempt = 0; attempt < 3; attempt += 1) {
          try {
            let doomed = keys;
            if (!doomed) {
              const res = await fetch(`${config.routes.root}cart.js`, { headers: { Accept: 'application/json' } });
              if (!res.ok) continue;
              const cart = await res.json();
              doomed = ((cart && cart.items) || []).filter((it) => it.properties && String(it.properties._tempUpload) === 'true').map((it) => it.key);
              if (!doomed.length) return;
            }
            const updates = {};
            doomed.forEach((k) => {
              updates[k] = 0;
            });
            const gone = await fetch(`${config.routes.root}cart/update.js`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
              body: JSON.stringify({ updates }),
            });
            if (gone.ok) return;
          } catch (_) {
            /* try again */
          }
        }
      };

      /* True once every byte has gone. From then on Shopify can add the line
         whatever this page hears back. */
      let sent = false;
      let json;
      try {
        json = await new Promise((resolve, reject) => {
          const xhr = new XMLHttpRequest();
          /* A stall, not a deadline. A slow upload that is still moving is never
             cut off: a 12MB photo over a weak 0.5 Mbps uplink takes over three
             minutes, and a fixed limit would fail it at 95% on every retry. The
             upload is given up only after UPLOAD_STALL_MS with no bytes leaving
             the phone, which is a dead connection, and once every byte has gone,
             after UPLOAD_SETTLE_MS with no answer. Either way it ends in the
             retry message instead of a bar that never moves again. */
          let timer = 0;
          let stalled = '';
          const arm = (ms, why) => {
            clearTimeout(timer);
            timer = setTimeout(() => {
              stalled = why;
              xhr.abort();
            }, ms);
          };
          const finish = () => {
            if (sent) return;
            sent = true;
            arm(UPLOAD_SETTLE_MS, 'had no answer');
            report({ phase: 'finishing', pct: 100 });
          };

          xhr.open('POST', `${config.routes.root}cart/add.js`);
          xhr.setRequestHeader('X-Giftship-Drawer-Ignore', 'true');

          /* Upload listeners go on before send(), or the browser never fires
             them. The percentage is floored and held at 99 until the last byte
             has gone, so the bar never says 100% while there is still sending
             to do. */
          xhr.upload.onprogress = (e) => {
            if (!sent) arm(UPLOAD_STALL_MS, 'stalled');
            if (!e.lengthComputable || !e.total) return;
            if (e.loaded >= e.total) return finish();
            report({ phase: 'sending', loaded: e.loaded, total: e.total, pct: Math.min(99, Math.floor((e.loaded / e.total) * 100)) });
          };
          xhr.upload.onload = finish;

          xhr.onload = () => {
            clearTimeout(timer);
            if (xhr.status < 200 || xhr.status >= 300) {
              reject(new Error(`upload responded ${xhr.status}`));
              return;
            }
            try {
              resolve(JSON.parse(xhr.responseText));
            } catch (e) {
              reject(e);
            }
          };
          xhr.onerror = () => {
            clearTimeout(timer);
            reject(new Error('upload failed on the network'));
          };
          xhr.onabort = () => {
            clearTimeout(timer);
            reject(new Error(stalled ? `upload ${stalled}` : 'upload aborted'));
          };

          arm(UPLOAD_STALL_MS, 'stalled');
          xhr.send(body);
        });
      } catch (e) {
        /* Every byte had gone, so Shopify may have added the line — or may yet
           — although this page never heard. Nothing else in the theme takes a
           `_tempUpload` line out, so the cart is swept here. A line Shopify
           adds only after this sweep would be missed; the wait above is long
           so that almost never happens. */
        if (sent) await dropTemp(null);
        throw e;
      }

      const line = (json && json.items && json.items[0]) || json;
      const url = line && line.properties && line.properties.Photo;

      /* Take the temp line back out whatever happened, so a failed read never
         leaves a stray box in the cart. */
      if (line && line.key) await dropTemp([line.key]);

      if (!url) throw new Error('upload returned no url');
      return url;
    },
    /* What MeterText needs to say "N more to check out". Items mode counts
       items against minItems; slots mode counts spaces against minimumSpaces. */
    need: itemsMode
      ? Math.max(0, (config.minItems || 0) - state.itemCount)
      : Math.max(0, (config.minimumSpaces || 0) - state.slotsUsed),
    money: window.BYO.money,
    slotWord: (size) => window.BYO.slotWord(size, config),
    slotsOf: (size) => window.BYO.slotsOf(size, config),
    qtyOf: (id) => (state.box.items.find((i) => i.id === id) || {}).qty || 0,
    fits: (p) => packer.fits(p, state.box, byId),
    fitsSize: (size) => packer.fitsSize(size, state.box, byId),
    reason: (p) => packer.reason(p, state.box, byId),
    remove: (id) => store.removeItem(id),
    /* The trash button clears the whole line; the stepper's minus decrements. */
    removeAll: (id) => store.setQty(id, 0),
    reset: () => store.reset(),
    add: (id, el) => {
      const p = byId[id];
      if (!p) return;

      const res = store.addItem(p);
      if (res.ok) {
        flyToBox(el);
        return;
      }

      setShake(`${id}:${Date.now()}`);
      const roomLeft = state.remaining > 1e-9;
      /* D70: the reason is the box, which already has things in it — not the
         product, which is why "it won't fit" read as a fault. */
      flash(
        roomLeft ? 'Your box is too full for this one' : 'Your box is full · take something out first',
        3400,
        roomLeft
          ? {
              label: 'Show what fits',
              onClick: () => {
                setFitOnly(true);
                setToast(null);
              },
            }
          : null,
        'error'
      );
    },
  };

  return b;
}

/**
 * PriceCallout — DESIGN-SPEC.md §3.2, the lime pill under the pills row.
 *
 * "Only on an empty box and not while editing." The copy is a merchant string
 * carrying two placeholders (CONFIG-SCHEMA.md §7): `{{ base }}` is the box
 * price and `{{ min }}` the minimum item count. The design bolds everything up
 * to the em dash — "**Every box starts at £10** — box, wrapping and handling"
 * — so the split is on the dash rather than on a second setting, which keeps
 * the merchant editing one sentence.
 */
export function PriceCallout({ b, config }) {
  if (config.priceCallout === false) return null;
  if (b.itemCount > 0 || b.editing) return null;

  const template =
    (config.copy && config.copy.callout) ||
    'Every box starts at [base] — our gift wrapping service. Pick at least [min] things. S, M, L and XL show how much room each takes: we curate every box to pack snugly and travel safely.';

  /* `[token]`, not `{{ token }}`. The theme editor refuses to save a setting
     whose value contains Liquid delimiters unless they resolve to a dynamic
     source, and it validates the schema default too — so the curly form
     blocked every save on this section, including adding a collection. The old
     form is still substituted, so a merchant who typed it once never sees raw
     braces on the page. `[threshold]` is honoured because the setting's own
     info has always offered it. */
  const sub = (name, value) =>
    new RegExp('\\[\\s*' + name + '\\s*\\]|\\{\\{\\s*' + name + '\\s*\\}\\}', 'g');

  /* The minimum is a count in a sentence, so it is a word (D70). */
  const text = template
    .replace(sub('base'), window.BYO.money(config.base || 0))
    .replace(sub('min'), window.BYO.words ? window.BYO.words(config.minItems || 0) : String(config.minItems || 0))
    .replace(sub('threshold'), window.BYO.money(config.freeShipping || 0));

  const cut = text.indexOf(' — ');
  const head = cut === -1 ? text : text.slice(0, cut);
  const tail = cut === -1 ? '' : text.slice(cut);

  return html`
    <div class="byo-v4-callout">
      <${Icon} name="tag" size=${16} />
      <span><strong>${head}</strong>${tail}</span>
    </div>
  `;
}

export function FillStage({ b, config, products, onHelp }) {
  const c = useCatalog(products, b);

  /* A new sort starts the grid from its top, where the new order begins. */
  const sorted = useRef(c.sort);
  useEffect(() => {
    if (sorted.current === c.sort) return;
    sorted.current = c.sort;
    const scroller = document.querySelector('.byo-v4-stage__scroll');
    if (!scroller) return;
    const reduced = typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    scroller.scrollTo({ top: 0, behavior: reduced ? 'auto' : 'smooth' });
  }, [c.sort]);


  const phone = usePhone();
  const pins = html`
    <div class="byo-v4-fill__pins">
      <${CategoryTabs} c=${c} config=${config} hasItems=${b.itemCount > 0} />
      <${SortPill} c=${c} />
    </div>
  `;

  return html`
    <div class="byo-v4-fill">
      ${phone && html`<${HeadSlot}>${pins}<//>`}
      <div class="byo-v4-fill__sticky">
        <div class="byo-v4-fill__inner">
          <div class="byo-v4-fill__headrow">
            <h2 class="byo-v4-fill__h2">${(config.copy && config.copy.fillHeading) || 'Fill Your Box'}</h2>
            ${onHelp &&
            html`<button class="byo-v4-fill__help" onClick=${onHelp} aria-label="Help" title="Help">
              <${Icon} name="help" size=${19} />
            </button>`}
          </div>
          ${!phone && pins}
          <${ResultsLine} c=${c} />
        </div>
      </div>
      ${/* D129: the count, under the pinned chips, scrolling with the products (phones). */ ''}
      <p class="byo-v4-fill__inner byo-v4-fill__count"><span class="byo-v4-results__count" aria-live="polite">${countLabel(c)}</span></p>

      <div class="byo-v4-fill__inner byo-v4-fill__shelves">
        <${PriceCallout} b=${b} config=${config} />
        ${/* D63: one grid with a small lime size tag on every picture, unless the
             merchant picks the size shelves back (Product layout). The shelves
             took too much room, and hid that there was more below. */ ''}
        ${(config.fillLayout || 'grid') === 'shelves'
          ? html`<${Shelves} c=${c} b=${b} />`
          : html`<${ProductGrid}
              list=${c.list}
              b=${b}
              c=${c}
              resetKey=${`${c.cat}|${(c.diet || []).join(',')}|${c.fitOnly ? 1 : 0}|${c.sort}`}
            />`}
      </div>
    </div>
  `;
}

/**
 * The app root. P3a is the Fill stage alone; P4 wraps this in the stage shell
 * (progress rail above, build bar below) that `ConceptShelves` draws.
 */
export function BuilderApp({ config, products }) {
  const b = useBuilder(config, products);
  const [submitting, setSubmitting] = useState(false);
  const [editing, setEditing] = useState(null);

  /* ConfirmStage's EditBanner and `nextFor`'s "Update <name>" label read these
     off `b`, so they must be attached before anything downstream reads it —
     `nextFor` runs further down this function and saw `undefined` when these
     sat with the other state. They are BuilderApp's, not useBuilder's, which
     has no idea a cart exists. */
  b.editing = editing ? editing.key : null;
  b.bundleName = editing ? editing.name : null;
  b.busy = submitting;

  const [stage, setStage] = useState('fill');

  /* Gating lives here rather than in the rail so the rail and the primary
     button raise the same refusal. A gated move does not change stage; it
     raises the short reason as a coral error toast (DESIGN-SPEC.md §3.5). */
  /**
   * Add To Cart. One box is one line: the base variant carrying the components
   * as `_gs_bundle_*` properties, which Giftship's cart transform expands
   * (GIFTSHIP-CONTRACT.md). A rejection is a real state, not an exception — it
   * keeps the box intact and raises the design's sold-out banner.
   */
  const submit = async (sure) => {
    if (submitting) return;
    /* Nothing reaches the cart half-finished: a keepsake box with no wrap
       (D70 made it easier to reach — the box can grow into one on Wrap It
       Up) is shown its error rather than added without one. */
    if (!b.canSubmit) {
      b.flashError(b.blockers[0] || 'Finish the earlier steps first');
      setShowErrors(true);
      return;
    }
    /* D121 (Alan): a box without a gift message is asked about once more, at
       the very end, so going without one is a choice and never a slip. */
    if (sure !== true && !b.box.messageOn) {
      setAskMessage(true);
      return;
    }
    setSubmitting(true);
    setFail(null);

    if (!bundleKey.current) bundleKey.current = newBundleKey();

    /* An edit updates that bundle's line; a new box adds one. */
    const res = editing
      ? await updateBox({ b, config, gs, bundleKey: bundleKey.current })
      : await submitBox({ b, config, gs, bundleKey: bundleKey.current });
    setSubmitting(false);

    if (res.ok) {
      await refreshCartCount();
      if (editing) {
        /* An edit is finished, not added: back to the cart it came from. */
        window.location.href = (config.routes && config.routes.cart) || '/cart';
        return;
      }
      setBoxesAdded((n) => n + 1);
      setAdded(true);
      return;
    }

    if (res.reason === 'rejected' && res.product) {
      setFail({ title: res.product.title, id: res.product.id });
      return;
    }
    b.flashError(res.message || 'That could not be added to the cart.');
  };

  const go = (to) => {
    if (stage === 'review' && to === 'review') {
      submit();
      return;
    }
    if (canGo(b, stage, to)) {
      setStage(to);
      return;
    }
    b.flashError(gateReason(b, stage));
    if (stage !== 'personal') return;

    /* The first error scrolls into view with a 72px offset, smooth (§4.2). */
    setShowErrors(true);
    requestAnimationFrame(() => {
      const first = document.querySelector('.byo-v4-touch.is-err, .byo-v4-err');
      if (!first) return;
      const y = first.getBoundingClientRect().top + window.scrollY - 72;
      window.scrollTo({ top: y, behavior: 'smooth' });
    });
  };

  const nx = nextFor(b, stage, config);

  const [inside, setInside] = useState(false);
  const [help, setHelp] = useState(false);
  const [showErrors, setShowErrors] = useState(false);
  const [askMessage, setAskMessage] = useState(false);
  const closeAsk = useCallback(() => setAskMessage(false), []);
  /* "Add One" / "Add A Message" (D121): back to Personalise with Gift Message
     switched on and its box ready to type in. */
  const addMessage = () => {
    setAskMessage(false);
    if (!b.box.messageOn) b.toggle('messageOn');
    setStage('personal');
    const reduced =
      typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    /* After the sheet has closed: it hands the focus back to Add To Cart as
       it goes, after paint, so a focus taken sooner is taken back. */
    const focus = (tries = 12) =>
      setTimeout(() => {
        const field = document.querySelector('[data-touch="message"] textarea');
        if (!field) {
          if (tries > 0) focus(tries - 1);
          return;
        }
        field.focus({ preventScroll: true });
        field.scrollIntoView({ block: 'center', behavior: reduced ? 'auto' : 'smooth' });
      }, 120);
    focus();
  };
  const [gs, setGs] = useState(null);
  const [added, setAdded] = useState(false);
  const [fail, setFail] = useState(null);
  const [boxesAdded, setBoxesAdded] = useState(0);
  const bundleKey = useRef(null);

  /* D65: "Edit this box" in the cart lands in the builder, not on the hero
     above it. The same position "Start building" scrolls to (reviews.js,
     DESIGN-SPEC.md §2.6), taken again once the page has loaded in case images
     above it moved it, and never after the customer has scrolled themselves.
     A plain link lands the same way with #build: the ready-made pages' "Build
     Your Own" (Alan, 30 Sep 2026). On the builder's own page (D144) every
     arrival lands this way, address or not: it is where "Start building"
     used to put it. */
  useEffect(() => {
    const q = new URLSearchParams(window.location.search);
    const ownPage = !!document.querySelector('[data-byo-v4-builder][data-byo-v4-own-page]');
    if (!ownPage && !q.get('byoedit') && !q.get('add') && window.location.hash !== '#build') return undefined;
    const land = () => {
      const el = document.querySelector('[data-byo-v4-builder]');
      if (!el) return;
      const r = el.getBoundingClientRect();
      const offset = Math.max(0, (window.innerHeight - r.height) / 2);
      /* On a phone the frame is a full screen tall: dock it at the sticky
         header's foot, as reviews.js's builderTop does (D129). */
      let hh = 0;
      if (window.matchMedia('(max-width: 989.98px)').matches) {
        const sticky = document.querySelector('[data-sticky-header]');
        const header = sticky && (sticky.closest('[id^="shopify-section-"]') || sticky);
        hh = parseFloat(el.style.getPropertyValue('--byo-v4-header-h')) || (header ? header.offsetHeight : 0);
      }
      window.scrollTo({ top: Math.max(0, r.top + window.scrollY - Math.max(offset, hh)), behavior: 'auto' });
    };
    let moved = false;
    const stop = () => {
      moved = true;
    };
    const again = () => {
      if (!moved) land();
    };
    land();
    const t = setTimeout(again, 700);
    window.addEventListener('load', again, { once: true });
    ['wheel', 'touchstart', 'keydown'].forEach((e) => window.addEventListener(e, stop, { once: true, passive: true }));
    return () => {
      clearTimeout(t);
      window.removeEventListener('load', again);
      ['wheel', 'touchstart', 'keydown'].forEach((e) => window.removeEventListener(e, stop));
    };
  }, []);

  /* ?add=<handle>[,<handle>…] — the stock journey (Claude Design, 27 Sep
     2026). "Add to box" on an edit page and "Start a box with this" on a stock
     item land here with those products already in a fresh box, and a note that
     says so, in the design's words: "<name> is in your box · add two more".
     Handles are the catalogue's own ids. Anything not on the shelves (sold out,
     not BYO stock) is skipped. The parameter comes off the address once used,
     so a reload does not add them again. The builder keeps no box in progress,
     so there is nothing to ask about first. */
  useEffect(() => {
    const raw = new URLSearchParams(window.location.search).get('add');
    if (!raw || !products.length) return;
    const ok = b.addMany(raw.split(',').map((h) => h.trim().toLowerCase()).filter(Boolean));
    try {
      const u = new URL(window.location.href);
      u.searchParams.delete('add');
      window.history.replaceState(window.history.state, '', u.pathname + u.search + u.hash);
    } catch (_) {
      /* an address we cannot rewrite only means a reload adds them again */
    }
    if (!ok.length) return;
    const w = window.BYO || {};
    const left = Math.max(0, (config.minItems || 0) - ok.length);
    const who =
      ok.length > 1
        ? `${w.Words ? w.Words(ok.length) : ok.length} things are`
        : `${String(b.byId[ok[0]].title).split(',')[0]} is`;
    b.flashNote(`${who} in your box · ${left ? `add ${w.words ? w.words(left) : left} more` : 'ready to personalise'}`);
  }, [products.length]); // eslint-disable-line react-hooks/exhaustive-deps

  /* Edit mode is entered by key: `?byoedit=<bundle key>`. The box is rebuilt
     from the line's own properties — its products from `_gs_bundle_ids` mapped
     back by variant id, and its personal touches from the keys they were
     written under — so the customer edits what they actually bought.

     **Restoring the touches is not optional.** Until 22 Sep this read the ids
     and nothing else, so opening a box from the cart silently dropped the gift
     message, the wrap, the photo, the song and the date: the customer saw their
     products, pressed Update, and the touches were gone from the order. The
     Edit link is live on the cart card, so every edit hit it. */
  useEffect(() => {
    const key = new URLSearchParams(window.location.search).get('byoedit');
    if (!key || !products.length) return undefined;

    let live = true;
    loadCart().then((cart) => {
      const found = findBundleLine(cart, key);
      if (!live || !found) return;

      const line = found.line;
      const ids = line.properties._gs_bundle_ids || [];
      const qtys = line.properties._gs_bundle_quantities || [];
      const byVariant = new Map(products.map((p) => [String(p.variantId), p]));

      /* The paid touches ride as bundle components so they are charged, so the
         extras' own variants are in `ids`. They are touches, not products: add
         them to the box as items and the customer gets "Printed Keepsake Photo"
         sitting in their box taking up a space. */
      const extras = config.extras || {};
      const extraIds = new Set(
        Object.keys(extras)
          .map((k) => extras[k] && extras[k].variantId)
          .filter(Boolean)
          .map(String)
      );

      b.reset();
      ids.forEach((vid, i) => {
        if (extraIds.has(String(vid))) return;
        const p = byVariant.get(String(vid));
        if (!p) return;
        const n = parseInt(qtys[i], 10) || 1;
        for (let k = 0; k < n; k += 1) b.add(p.id);
      });

      /* Read by meaning, not by key: the titles come from Giftship's own form,
         so `readTouchProps` matches them the same way the write chose them
         (byo-v4-core.js). */
      const touches = (window.BYO && window.BYO.readTouchProps ? window.BYO.readTouchProps(line) : {}) || {};

      /* How it was given comes back from our own marker. D95: a wrap no
         longer means in person, because the keepsake box comes with the box's
         size alone; only a line from before the marker is read the old way.
         The keepsake itself is worked out from the size, so restoring the
         items and the wrap restores it. */
      const given = line.properties._byo_given;
      if (given ? given === 'in person' : touches.wrap || touches.inPerson) b.setInPerson(true);
      if (touches.wrap) b.setWrap(touches.wrap);
      if (touches.photo) {
        const bits = String(touches.photo).split('/');
        const name = decodeURIComponent(bits[bits.length - 1] || '').split('?')[0];
        b.setPhoto({ on: true, file: { name, url: touches.photo } });
      }
      if (touches.song) b.setSong({ on: true, text: touches.song });
      if (touches.date) b.setDate({ on: true, value: touches.date });
      if (touches.message) {
        b.toggle('messageOn');
        b.setMessage(touches.message);
        if (touches.from) b.setFrom(touches.from);
      }
      if (touches.recipient) b.setRecipient(touches.recipient);

      bundleKey.current = key;
      setEditing({ key, name: bundleName(cart, key) });
      setStage('review');
    });

    return () => {
      live = false;
    };
  }, [products]);
  const wraps = (gs && gs.wraps) || [];

  /* The three wraps are Giftship's image step, never settings
     (sections/byo-v4-builder.liquid:404). Loaded once, lazily; a failure
     leaves the picker to say so rather than drawing empty tiles. */
  useEffect(() => {
    const id = config.giftship && config.giftship.builderId;
    if (!id || !window.BYOGiftship) return;
    let live = true;
    window.BYOGiftship.loadBuilder(id)
      .then((r) => {
        /* The whole parsed builder is kept, not just the wraps: the cart
           write builds its property keys from Giftship's own step and field
           titles (byo-v4-submit.js). */
        if (!live || !r) return;
        setGs(r);
        /* The cart draws the chosen wrap on the bundle card, and only the
           wrap's *name* travels in the line properties. Stashing name -> image
           here saves the cart a Giftship call to render one 72px square
           (DEVIATIONS.md D48). */
        try {
          sessionStorage.setItem(
            'byo-v4-wraps',
            JSON.stringify((r.wraps || []).map((w) => ({ name: w.name, img: w.img })))
          );
        } catch (_) {
          /* private mode: the cart falls back to the box icon */
        }
      })
      .catch(() => {});
    return () => {
      live = false;
    };
  }, [config]);

  /* Errors appear on Continue only (DESIGN-SPEC.md §4.2), and clear on every
     stage change so a returning customer is not met by red. */
  useEffect(() => {
    setShowErrors(false);
  }, [stage]);

  /* The theme's site header is sticky and overlays the top of the screen. The
     screen is 100dvh, so when its foot is locked to the viewport foot its head
     -- the progress rail -- sits behind that header. Measure the header and
     shorten the screen by it, so the rail clears it at every scroll position.
     The design has no such header over its screen; this is the theme's. */
  useEffect(() => {
    const mount = document.querySelector('[data-byo-v4-mount]');
    if (!mount) return undefined;

    /* Scan for the theme's sticky chrome rather than guess at class names or
       probe a point: once the screen's top reaches the viewport top, a point
       probe hits the builder itself. On this theme it is
       `.shopify-section-group-header`, sticky at top: 0, 78px tall -- but the
       scan keeps working if that changes. */
    /* The scan reads every element's computed style, so it runs on load and
       once more at 600ms; a resize (on a phone, Safari's toolbars moving as
       the page scrolls) only re-reads the header it found. */
    let header = null;
    const scan = () => {
      let h = 0;
      header = null;
      document.querySelectorAll('body *').forEach((el) => {
        if (mount.contains(el)) return;
        const cs = window.getComputedStyle(el);
        if (cs.position !== 'sticky' && cs.position !== 'fixed') return;
        if (parseFloat(cs.top || '0') > 4) return;
        const r = el.getBoundingClientRect();
        if (r.height > 10 && r.height < 240 && r.height > h) {
          h = r.height;
          header = el;
        }
      });
    };
    const measure = () => {
      if (!header || !header.isConnected) scan();
      const v = `${Math.round(header ? header.getBoundingClientRect().height : 0)}px`;
      /* Written only when it changes, for the same reason as the lift. */
      if (mount.style.getPropertyValue('--byo-v4-header-h') !== v) mount.style.setProperty('--byo-v4-header-h', v);
      const section = mount.closest('[data-byo-v4-builder]');
      if (section && section.style.getPropertyValue('--byo-v4-header-h') !== v) section.style.setProperty('--byo-v4-header-h', v);
      dock();
    };

    /* Keep the bar at the screen's foot as the page moves (D129). Not while
       browse mode glides: the frame is mid-transform and owns the lift. */
    const dock = () => {
      const section = mount.closest('[data-byo-v4-builder]');
      if (!section || section.classList.contains('is-gliding')) return;
      const hh = parseFloat(mount.style.getPropertyValue('--byo-v4-header-h')) || 0;
      applyDock(mount, overhang(mount, hh));
    };

    /* The sheets are absolutely positioned inside the screen and must stop at
       the build bar, which changes height when it opens. Publish its height so
       the panel can sit on top of it rather than behind it. */
    const bar = () => document.querySelector('.byo-v4-bar');
    const measureBar = () => {
      const el = bar();
      mount.style.setProperty('--byo-v4-bar-h', `${el ? Math.round(el.getBoundingClientRect().height) : 0}px`);
    };

    let ro = null;
    if (window.ResizeObserver) {
      ro = new window.ResizeObserver(measureBar);
      const el = bar();
      if (el) ro.observe(el);
    }

    measure();
    measureBar();
    window.addEventListener('resize', measure);
    window.addEventListener('resize', measureBar);
    window.addEventListener('scroll', dock, { passive: true });
    const t = setTimeout(() => {
      header = null;
      measure();
      measureBar();
    }, 600);
    return () => {
      window.removeEventListener('scroll', dock);
      window.removeEventListener('resize', measure);
      window.removeEventListener('resize', measureBar);
      if (ro) ro.disconnect();
      clearTimeout(t);
    };
  }, []);

  /* Each stage opens at its own top. The rail is the first thing in the
     scroller, so carrying Fill's offset into Personalise hid it entirely. */
  const scroller = useRef(null);
  const [more, setMore] = useState(false);

  /* Is there more below? DESIGN-SPEC.md §3.2 gives the pills row a 56px edge
     fade with a chevron that fades out at the end; the same affordance runs
     vertically here, because Confirm's cards can end under the bar with
     nothing to say so. DEVIATIONS.md D36. */
  const checkMore = useCallback(() => {
    const el = scroller.current;
    if (!el) return;
    /* The scroller ends with the header's height of room (D129). Docked,
       it sits behind the lifted bar; browsing, it is real space, not more. */
    const s = el.closest('[data-byo-v4-builder]');
    const hh = s ? parseFloat(s.style.getPropertyValue('--byo-v4-header-h')) || 0 : 0;
    const room = window.matchMedia('(max-width: 989.98px)').matches ? hh - dockPx : 0;
    setMore(el.scrollHeight - el.scrollTop - el.clientHeight - room > 8);
  }, []);

  /* Browse mode (D129, Alan, 10 Oct 2026: "hiding the sticky header"). On a
     phone the frame is a full screen tall and docks under the site header
     (#build, D70's snap). Scrolling down the stage takes the page the
     header's height further: the header slides away (DESIGN-SPEC.md §2.1's
     own classes, and its 60% test agrees) and the frame fills the screen.
     Scrolling back up, reaching the top, or a keyboard reaching the header,
     brings it back. The page moves at once and the frame glides the
     difference back by transform, in step with the header's 0.3s, while the
     bar stays put at the foot -- so nothing changes size and nothing counts
     as a layout shift. Never on the packing scene, never on desktop. */
  useEffect(() => {
    const el = scroller.current;
    const section = document.querySelector('[data-byo-v4-builder]');
    const frame = section && section.querySelector('[data-byo-v4-mount]');
    if (!el || !frame || added) return undefined;
    const phone = window.matchMedia('(max-width: 989.98px)');
    const still = window.matchMedia('(prefers-reduced-motion: reduce)');
    const themeHeader = () => {
      const sticky = document.querySelector('[data-sticky-header]');
      if (sticky) return sticky.closest('[id^="shopify-section-"]') || sticky;
      const h = document.querySelector('.shopify-section-group-header-group [role="banner"]');
      return h ? h.closest('[id^="shopify-section-"]') || h : null;
    };
    const headerH = () => parseFloat(section.style.getPropertyValue('--byo-v4-header-h')) || 0;
    const setDock = (px) => applyDock(frame, px);
    let timer = 0;
    let run = 0;
    let changed = 0;
    const glide = (by, hidden) => {
      changed = Date.now();
      run = 0;
      const header = themeHeader();
      if (header) {
        header.classList.add('byo-v4-header-slide');
        header.classList.toggle('byo-v4-header-hidden', hidden);
      }
      clearTimeout(timer);
      section.classList.remove('is-gliding');
      frame.style.transform = '';
      window.scrollTo(window.scrollX, window.scrollY + by);
      const hh = headerH();
      const after = overhang(frame, hh);
      if (still.matches) return setDock(after);
      /* Hold the frame where it was, with the bar's lift unchanged, then let
         both go together. */
      frame.style.transform = `translateY(${by}px)`;
      void frame.offsetHeight;
      section.classList.add('is-gliding');
      frame.style.transform = '';
      setDock(after);
      timer = setTimeout(() => {
        section.classList.remove('is-gliding');
        setDock(overhang(frame, headerH()));
      }, 360);
      return undefined;
    };
    const browsing = () => {
      const top = section.getBoundingClientRect().top;
      return top > -2 && top < headerH() - 1;
    };
    const leave = () => {
      if (phone.matches && browsing()) glide(section.getBoundingClientRect().top - headerH(), false);
    };
    /* A deliberate scroll each way, not a single event: 24px on down to go,
       48px back up to return, and a pause after each change. A finger
       drifting while reading had flipped it back and forth, the chips
       jumping the header's height each time (Alan's iPhone, 11 Oct). */
    let last = el.scrollTop;
    const onScroll = () => {
      const y = el.scrollTop;
      const d = y - last;
      last = y;
      if (!d || !phone.matches || section.classList.contains('is-gliding')) return undefined;
      run = Math.sign(d) === Math.sign(run) ? run + d : d;
      if (y < 24) return leave();
      if (Date.now() - changed < 500) return undefined;
      if (run <= -48) return leave();
      const top = section.getBoundingClientRect().top;
      const fills = frame.offsetHeight >= window.innerHeight - 2;
      if (run >= 24 && fills && top > 1 && top <= headerH() + 4) glide(top, true);
      return undefined;
    };
    const onFocus = (e) => {
      const header = themeHeader();
      if (header && header.contains(e.target)) leave();
    };
    el.addEventListener('scroll', onScroll, { passive: true });
    document.addEventListener('focusin', onFocus);
    return () => {
      el.removeEventListener('scroll', onScroll);
      document.removeEventListener('focusin', onFocus);
      leave();
    };
  }, [stage, added]);

  /* A stage change can draw a new bar or sheet: give it the current lift. */
  useEffect(() => {
    const frame = document.querySelector('[data-byo-v4-mount]');
    if (frame) applyDock(frame, dockPx);
  });

  useEffect(() => {
    if (scroller.current) scroller.current.scrollTop = 0;

    /* A sheet belongs to the stage it was opened from. Left open across a
       stage change it covers the new one entirely on a phone — What's Inside
       hid the whole Personalise step. Quick Look goes with them. */
    setInside(false);
    setHelp(false);
    b.closePeek();

    /* After the new stage has painted, not before. */
    const id = requestAnimationFrame(checkMore);
    return () => cancelAnimationFrame(id);
  }, [stage, added, checkMore]);

  /* Content grows and shrinks as touches open and items come and go. */
  useEffect(() => {
    const el = scroller.current;
    if (!el || !window.ResizeObserver) return undefined;
    const ro = new window.ResizeObserver(checkMore);
    Array.from(el.children).forEach((c) => ro.observe(c));
    return () => ro.disconnect();
  }, [stage, checkMore]);

  /* "Show what fits" turns on the fit filter and closes anything covering the
     shelves — the sheet and Quick Look both (BUILD-PLAN P3 watch-for). It also
     returns to Fill: the nudge that offers it appears whenever the box is below
     the minimum, which is reachable from Personalise and Confirm too, and the
     filtered shelves are on Fill. DESIGN-SPEC.md §3.6 puts the button on the
     nudge unconditionally. */
  const showFits = () => {
    b.setFitOnly(true);
    setInside(false);
    b.closePeek();
    setStage('fill');
  };

  if (added) {
    return html`
      <div class="byo-v4-stage byo-v4-stage--added" data-byo-screen>
        <${AddedScreen}
          b=${b}
          config=${config}
          quick=${boxesAdded > 1}
          boxesInCart=${boxesAdded}
          onBuildAnother=${() => {
            /* A fresh box needs a fresh bundle key, or the next add would
               update this one in place instead of joining the cart. */
            bundleKey.current = null;
            setEditing(null);
            b.reset();
            setAdded(false);
            setStage('fill');
          }}
        />
      </div>
    `;
  }

  /* The design's `Screen`: a flex column filling the stage, the journey
     scrolling inside it, the bar pinned at its foot by being the last
     flex-shrink:0 child. It is NOT a sticky bar on a scrolling page — P0 built
     `.byo-v4-builder__stage` as a fixed-height screen (100dvh, overflow hidden)
     and an overflow-hidden ancestor cannot hold a sticky child anyway. */
  return html`
    <div class="byo-v4-stage" data-byo-screen>
      ${/* The rail is screen chrome, not content: concept-e-shelves-v2.jsx:27
           gives it its own flexShrink:0 band above the scroller, so it stays
           visible at every scroll position. */ ''}
      <div class="byo-v4-stage__head">
        <div class="byo-v4-fill__inner">
          <${ProgressRail}
            b=${b}
            stage=${stage}
            onGo=${go}
            titles=${{ ...STAGE_TITLES, fill: (config.copy && config.copy.fillHeading) || STAGE_TITLES.fill }}
            onHelp=${() => setHelp(true)}
          />
        </div>
        ${stage === 'fill' && html`<div class="byo-v4-stage__pins" data-byo-v4-head-slot></div>`}
      </div>

      <div class="byo-v4-stage__scroll" ref=${scroller} onScroll=${checkMore}>

      ${stage === 'fill' &&
      html`<${FillStage} b=${b} config=${config} products=${products} onHelp=${() => setHelp(true)} />`}

      ${stage !== 'fill' &&
      html`<div class="byo-v4-fill__inner"><${StageHead} stage=${stage} onHelp=${() => setHelp(true)} /></div>`}

      ${stage === 'personal' &&
      html`<${PersonaliseStage} b=${b} config=${config} wraps=${wraps} showErrors=${showErrors} />`}

      ${stage === 'review' &&
      html`<${ConfirmStage}
        b=${b}
        config=${config}
        wraps=${wraps}
        showErrors=${showErrors}
        onEdit=${(to) => setStage(to)}
        onAddMessage=${addMessage}
        fail=${fail}
        onSwap=${() => {
          if (fail) b.removeAll(fail.id);
          setFail(null);
          setStage('fill');
        }}
        onDismissFail=${() => setFail(null)}
      />`}

      </div>

      <div class=${`byo-v4-stage__fade${more ? ' is-on' : ''}`} aria-hidden="true">
        <${Icon} name="chevD" size=${16} />
      </div>

      ${/* The Quick Look and the toast are drawn here, beside the bar and the
           sheets, not inside FillStage. iPhone Safari clips a position:fixed
           element to the touch-scrolling box it sits in, so inside
           .byo-v4-stage__scroll the Quick Look was cut to the scroller: the
           rail and the bar drew over it and its close was out of reach
           (DEVIATIONS.md D60). */ ''}
      ${stage === 'fill' && html`<${QuickView} b=${b} />`}
      <${Toast} msg=${b.toast} />

      <${BuildBar}
        b=${b}
        config=${config}
        stage=${stage}
        back=${stage !== 'fill'}
        onBack=${() => setStage(prevStage(stage))}
        next=${nx.label}
        onNext=${() => (stage === 'review' ? submit() : go(nextStage(stage)))}
        nextDisabled=${nx.disabled}
        hint=${nx.hint}
        onTray=${() => setInside(true)}
        onHelp=${() => setHelp(true)}
      />

      <${InsideSheet}
        b=${b}
        config=${config}
        open=${inside}
        onClose=${() => setInside(false)}
        onFit=${showFits}
      />
      <${HelpSheet} b=${b} config=${config} open=${help} stage=${stage} onClose=${() => setHelp(false)} />
      <${Sheet} open=${askMessage} onClose=${closeAsk} display title="Send it without a message?" z=${50}>
        <div class="byo-v4-ask">
          <p class="byo-v4-ask__p">There’s no gift message on this box, so it’ll arrive without a message card. Adding one is free.</p>
          <div class="byo-v4-ask__btns">
            <${Btn} variant="primary" full onClick=${addMessage}>Add A Message<//>
            <${Btn}
              variant="outline"
              full
              onClick=${() => {
                setAskMessage(false);
                submit(true);
              }}
              >Continue Without A Message<//
            >
          </div>
        </div>
      <//>
    </div>
  `;
}

/**
 * Fetch every configured collection, normalise and merge.
 *
 * One request per pill, de-duplicated by handle in the browser
 * (THEME-INTEGRATION.md §4). A pill that fails is logged and skipped rather
 * than taking the whole grid down with it — but if every one fails the caller
 * gets an empty list and renders the error state, never a silent blank grid
 * (GIFTSHIP-API.md §3).
 */
export async function loadProducts(config) {
  const handles = (config.collections || []).map((c) => c.handle);
  const results = await Promise.all(
    handles.map(async (handle) => {
      try {
        /* No Accept: application/json here. That header makes Shopify answer with
           its own collection JSON and ignore ?view=byo-v4, returning
           { collection: … } with no products array. res.ok is still true, so the
           catch below never fires and every shelf renders empty. */
        const res = await fetch(`${config.routes.root}collections/${handle}?view=byo-v4`);
        if (!res.ok) throw new Error(`${handle} responded ${res.status}`);
        return window.BYO.normaliseCollection(await res.json(), config);
      } catch (error) {
        console.error('[byo-v4] could not load collection', handle, error);
        return null;
      }
    })
  );

  const ok = results.filter(Boolean);
  return { products: window.BYO.mergeProducts(ok), failed: results.length - ok.length, total: results.length };
}

/** Mount into the section's stage element. */
export async function mount(root) {
  const config = window.BYO && window.BYO.config;
  if (!root || !config) return;

  const { products, failed, total } = await loadProducts(config);

  if (total > 0 && failed === total) {
    root.innerHTML = '';
    const err = document.createElement('div');
    err.className = 'byo-v4-err';
    err.setAttribute('role', 'alert');
    err.innerHTML =
      '<p class="byo-v4-err__msg">We can’t load the builder right now.</p>' +
      '<button type="button" class="byo-v4-btn byo-v4-btn--outline byo-v4-btn--sm">Try again</button>';
    err.querySelector('button').addEventListener('click', () => mount(root));
    root.appendChild(err);
    return;
  }

  root.innerHTML = '';
  render(html`<${BuilderApp} config=${config} products=${products} />`, root);
}

export default { mount, BuilderApp, FillStage, useBuilder, useCatalog, loadProducts };

/* Published for the siblings that used to `import` this file. See the
   section's loader: relative imports drop the ?v= and are served from a
   year-long CDN cache, so a sibling could be a build behind. */
Object.assign((window.BYOV4 = window.BYOV4 || {}), { BuilderApp, CategoryTabs, FillStage, PriceCallout, ProductCard, ProductGrid, ResultsLine, SHELF_ORDER, SORTS, Shelves, SortPill, loadProducts, mount, useBuilder, useCatalog });
